/**
 * The bell in the workspace bar — what the platform told you, and a way in.
 *
 * Isaiah Cotto, 2026-09-23: "We should be able to refresh the tables to see
 * the assignments after receiving the assignment notification via email and
 * in the platform. I do not want slow data."
 *
 * == Why it lives in the bar =============================================
 *
 * One bell, above every workforce workspace, rather than one per screen. A
 * Patient Advocate who is looking at a resident still needs to hear that
 * another lead landed; a UMED operator working a packet still needs to hear
 * that a PA let go of a case. Notifications are addressed to a person, not
 * to a page, so they belong on the furniture that follows the person.
 *
 * == It links, it does not re-implement ==================================
 *
 * Each notification carries the ids that caused it. Opening one navigates to
 * that case in the workspace that owns it; it does not render a case here.
 * A notification about something this build has no route for is still shown
 * and still readable — it simply does not become a link.
 *
 * == Redaction is shown, not hidden ======================================
 *
 * When the server withheld the words (the reader is not cleared for resident
 * detail today), the row still appears and says so. Dropping it would tell a
 * returning operator that nothing happened, which is false.
 *
 * == A disclosure, not a modal ===========================================
 *
 * The panel is a popover: the page behind stays live, focus is not trapped, and a click elsewhere closes it. It is
 * therefore not a dialog (no role=dialog, no aria-haspopup=dialog); the button is a disclosure (aria-expanded +
 * aria-controls) and the panel a named group. Escape closes it and returns focus to the bell.
 */
import React, { useEffect, useId, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Notification, NotificationState } from '../services/notificationsApi';

type Props = { state: NotificationState };

/** Where a notification's ids lead. Unknown shapes are not links. */
function destinationFor(n: Notification): string | null {
  const d = (n.data || {}) as Record<string, unknown>;
  const umed = typeof d.umedCaseId === 'string' ? d.umedCaseId : null;
  const advocate = typeof d.caseId === 'string' ? d.caseId : (typeof d.advocateCaseId === 'string' ? d.advocateCaseId : null);
  const review = typeof d.reviewId === 'string' ? d.reviewId : null;
  // A benefits check waiting for a VA (2026-10-22): the Needs verification list, that request open.
  const verification = typeof d.verificationId === 'string' ? d.verificationId : null;
  // My work, explicitly: the UMED workspace opens on the Kanban board since 2026-10-14.
  // An administrator viewed the portal as you (read-only Sign in as, 2026-10-26): your account activity.
  if (d.kind === 'SIGN_IN_AS') return '/account/activity';
  // Medicaid pending, marked by a provider in the field (2026-10-27): no case exists, so the PA workspace.
  if (d.kind === 'MEDICAID_PENDING') return '/portal/patient-advocate';
  if (umed) return `/portal/umed?view=work&case=${encodeURIComponent(umed)}`;
  if (verification) return `/portal/manifest?view=verification&open=${encodeURIComponent(verification)}`;
  // A benefits check a reviewer asked for was answered: the list they asked from (they cannot open the PA case).
  if (d.manifestView === 'verification') return '/portal/manifest?view=verification';
  if (advocate) return `/portal/patient-advocate?case=${encodeURIComponent(advocate)}`;
  if (review) return `/portal/manifest?review=${encodeURIComponent(review)}`;
  return null;
}

function when(iso: string): string {
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return '';
  const mins = Math.round((Date.now() - t) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours} h ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export default function NotificationBell({ state }: Props) {
  const { feed, markRead, markAllRead, refresh } = state;
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const panelId = useId();

  useEffect(() => {
    if (!open) return undefined;
    // Opening is an explicit ask for the current state, not the cached one.
    refresh();
    const onDown = (e: MouseEvent) => { if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false); };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { setOpen(false); buttonRef.current?.focus(); } };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open, refresh]);

  const unread = feed?.unread ?? 0;
  const items = feed?.items ?? [];

  const openOne = (n: Notification) => {
    markRead(n.id);
    const to = destinationFor(n);
    setOpen(false);
    if (to) navigate(to);
  };

  return (
    <div className="relative" ref={wrapRef}>
      <button
        ref={buttonRef}
        type="button"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((o) => !o)}
        className="relative rounded px-2 py-1 text-[13px] leading-5 text-white outline-none hover:bg-white/10 focus-visible:ring-2 focus-visible:ring-[#7fd1c3]"
      >
        <span aria-hidden>🔔</span>
        <span className="sr-only">
          {unread > 0 ? `Notifications, ${unread} unread` : 'Notifications, none unread'}
        </span>
        {unread > 0 && (
          <span
            aria-hidden
            className="absolute -right-0.5 -top-0.5 min-w-[1.1rem] rounded-full bg-[#e2445c] px-1 text-center text-[10px] font-semibold leading-[1.1rem] text-white"
          >
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          id={panelId}
          role="group"
          aria-label="Notifications"
          className="absolute right-0 z-50 mt-1 w-[22rem] max-w-[calc(100vw-2rem)] overflow-hidden rounded-md border border-black/10 bg-white text-[13px] text-[#10252d] shadow-lg"
        >
          <div className="flex items-center justify-between border-b border-black/10 px-3 py-2">
            <strong className="text-[12px] uppercase tracking-wide text-[#60747b]">Notifications</strong>
            {unread > 0 && (
              <button type="button" onClick={markAllRead} className="rounded px-1.5 py-0.5 text-[12px] text-[#136f63] hover:bg-[#f0f5f4] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#136f63]">
                Mark all read
              </button>
            )}
          </div>

          {items.length === 0 ? (
            <p className="px-3 py-4 text-[#60747b]">
              Nothing yet. You will be told here — and by email — when a lead is assigned to you or work is handed to you.
            </p>
          ) : (
            <ul className="max-h-[26rem] overflow-y-auto">
              {items.map((n) => {
                const linked = destinationFor(n) !== null;
                return (
                  <li key={n.id} className={`border-b border-black/5 last:border-b-0 ${n.is_read ? '' : 'bg-[#f2f9f7]'}`}>
                    <button
                      type="button"
                      onClick={() => openOne(n)}
                      className="block w-full px-3 py-2 text-left hover:bg-[#f0f5f4] focus:bg-[#f0f5f4] focus:outline-none"
                    >
                      <span className="flex items-baseline justify-between gap-2">
                        <strong className={n.is_read ? 'font-medium' : 'font-semibold'}>{n.title}</strong>
                        <span className="shrink-0 text-[11px] text-[#60747b]">{when(n.created_at)}</span>
                      </span>
                      {n.message && <span className="mt-0.5 block text-[#33474e]">{n.message}</span>}
                      {n.redacted && (
                        <span className="mt-0.5 block text-[11px] text-[#8a5a00]">Resident detail withheld — you are not on the PHI operator list.</span>
                      )}
                      {!linked && !n.redacted && (
                        <span className="mt-0.5 block text-[11px] text-[#60747b]">Open the workspace to act on this.</span>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
